import { useLingui } from "@lingui/react/macro";
import { ParkingOption } from "@offstreetparking/design-system/dataDisplay/ParkingOption";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { Checkbox } from "@offstreetparking/design-system/forms";
import { useLocale } from "@offstreetparking/design-system/providers";
import { classNames } from "@offstreetparking/design-system/utils";
import type { WizardParkingChoice } from "../../CreatePermitWizard/PermitContextProvider/types";
import { ParkingChoiceTag } from "../../CreatePermitWizard/components/ParkingTimeForm/components/ParkingChoiceTag";
import { CapacityFullBadge } from "../CapacityFullBadge";
type ParkingUpcomingProps = {
	parkingOptions: WizardParkingChoice[];
	timezone: string;
};
export const ParkingUpcoming = ({ parkingOptions, timezone }: ParkingUpcomingProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	return (
		<div className="flex flex-col gap-4">
			{parkingOptions.map((choice) => {
				const isUpcoming = choice.rangeStart && new Date(choice.rangeStart) > new Date();
				const disabled = isUpcoming || choice.capacity === "full";
				const title = (choice.title ?? "").length > 0 ? choice.title : t`Date & Time`;
				const ariaLabel = (() => {
					let label = title;

					// Add capacity status
					if (choice.capacity === "full") {
						label += `, ${t`No Spots Left`}`;
					}

					// Add tag message (opens/closes status)
					if (choice.rangeStart && choice.rangeEnd) {
						const intervals = choice.time.intervals ?? [];
						const lastInterval = intervals[intervals.length - 1];
						if (isUpcoming || (lastInterval && lastInterval.endDateTimeISO !== choice.rangeEnd)) {
							const date = isUpcoming ? choice.rangeStart : choice.rangeEnd;
							const dateStr = formatDate({
								date,
								formatType: "Abbreviated_Date",
								timezone,
								language: locale,
							});
							const timeStr = formatDate({ date, formatType: "Time", timezone, language: locale });
							const tagMessage = isUpcoming
								? t`Option available ${dateStr} at ${timeStr}`
								: t`Option available until ${dateStr} at ${timeStr}`;
							label += `, ${tagMessage}`;
						}
					}
					return label;
				})();
				return (
					<div key={choice.permitTypeId} className="relative">
						<fieldset
							className={classNames("w-full min-w-0 rounded-md border border-neutral-faded")}
							aria-label={ariaLabel}
							disabled={!!disabled}
						>
							<div className="flex flex-col items-start rounded-t-md bg-neutral-faded p-2">
								<div className="flex w-full items-center justify-between gap-4">
									<Checkbox
										name="selectedParkingChoice"
										value={false}
										isDisabled
										label={title}
										labelClassName={classNames(
											"font-bold whitespace-nowrap",
											disabled ? "opacity-50" : "",
										)}
									/>

									{choice.capacity === "full" && <CapacityFullBadge />}
								</div>

								<ParkingChoiceTag choice={choice} timezone={timezone} />
							</div>
							<div className={classNames("space-y-2 p-2", disabled ? "opacity-50" : "")}>
								<ParkingOption
									timezone={timezone}
									isInstant={!!choice.time.startDate?.instant && !!choice.time.startTime?.instant}
									permitIntervals={choice.time.intervals}
									context={choice.context}
								/>
							</div>
						</fieldset>
					</div>
				);
			})}
		</div>
	);
};
